<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-user ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('requested_user') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="isSaving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        <span>{{ headerFirstPart }}</span>
                        <span v-if="headerSecondPart" class="record-title">{{ headerSecondPart }}</span>
                    </v-toolbar-title>
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <ActionBtn
                            color="primary"
                            :disabled="!isValid || record.is_generated || record.rejected"
                            :loading="isSaving"
                            @click.stop="save"
                        >
                            <KpIcon icon-cls="save" color="white" />
                            {{ $t('save') }}
                        </ActionBtn>
                        <ActionBtn color="primary" @click.stop="cancel">
                            <KpIcon icon-cls="x" color="white" />
                            {{ $t('schliessen') }}
                        </ActionBtn>
                        <ActionBtn color="primary" @click.stop="initiateDelete">
                            <KpIcon icon-cls="trash" color="white" />
                            {{ $t('delete') }}
                        </ActionBtn>
                        |
                        <ActionBtn
                            color="primary"
                            :disabled="!isValid || record.is_generated || record.rejected"
                            :loading="isSaving"
                            class="mr-1"
                            @click.stop="generateUserOrganizer"
                        >
                            <KpIcon icon-cls="like" color="white" :tooltip="$t('generate_user_organizer')" />
                        </ActionBtn>
                        <ActionBtn
                            color="primary"
                            :disabled="!isValid || record.is_generated || record.rejected"
                            :loading="isSaving"
                            @click.stop="showRejectDlg = true"
                        >
                            <KpIcon icon-cls="unlike" color="white" :tooltip="$t('reject_user')" />
                        </ActionBtn>
                    </v-slide-group>
                    <v-spacer />
                    {{ record.statusLabel }}
                    <v-spacer />
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="navItem in navItems"
                        :key="navItem.id"
                        :disabled="!Number(record?.getId())"
                        class="nav-tab"
                        :to="{ name: navItem.routeName }"
                        exact
                    >
                        <KpIcon :icon-cls="`${navItem.icon}`" color="grey" active-color="black" left />
                        {{ navItem.titleFn ? navItem.titleFn() : navItem.title }}
                    </v-tab>
                </v-tabs>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    :record="record"
                    @input="isValid = $event"
                    @saving="isSaving = $event"
                    @save="save"
                />
            </div>
        </v-card>
        <PromptDialog
            v-if="showRejectDlg"
            v-model="showRejectDlg"
            :title="$t('reject_user')"
            :message="$t('reject_reason')"
            :confirm-label="$t('reject').toLowerCase()"
            @confirm="rejectUser"
        />
        <ConfirmDialog
            v-if="showDelDlg"
            v-model="showDelDlg"
            :title="$t('rec_delete')"
            :message="$t('confirm_rec_delete')"
            @confirm="deleteRec"
        />
    </div>
</template>

<script>
import { RequestedUser, RequestedUserCollection } from '@/models/RequestedUser';
import { getRoles } from '@/models/Role';
import { mapActions } from 'vuex';
import getRulesMixin from '@/mixins/get-form-rules';
import { errToStr, setBrowserTitle } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';

export default {
    name: 'RequestedUserEdit',
    mixins: [getRulesMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.save();
        if (to.params.id !== from.params.id) {
            this.record = null;
            this.loadRecord(to.params.id);
        }
        next();
    },

    data() {
        return {
            showRejectDlg: false,
            showDelDlg: false,
            record: null,
            isValid: false,
            isSaving: false,
            roles: getRoles(),
            navItems: [
                {
                    id: 'user_data',
                    routeName: 'requested_user_edit',
                    icon: 'userin',
                    title: this.$t('user'),
                },
                {
                    id: 'organizer_data',
                    routeName: 'requested_user_edit_organizer',
                    icon: 'speaker',
                    title: this.$t('organizer'),
                },
                {
                    id: 'contact_data',
                    routeName: 'requested_user_edit_contact',
                    icon: 'admin',
                    title: this.$t('contactperson'),
                },
            ],
        };
    },

    computed: {
        headerFirstPart() {
            return `${this.$t('edit_requested_user')}${this.headerSecondPart ? `: ` : ''}`;
        },
        headerSecondPart() {
            return this.record?.fullname;
        },
        browserTitle() {
            if (this.headerSecondPart) {
                return `${this.headerSecondPart} - ${this.$t('requested_user')} - ${this.activeNavItem?.title}`;
            } else {
                return this.headerFirstPart;
            }
        },
        activeNavItem() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((navItem) => navItem.routeName === actRouteName);
            if (items?.length > 0) {
                return items[0];
            }
            return null;
        },
    },

    watch: {
        browserTitle: {
            handler() {
                setBrowserTitle(this.getBrowserTitle());
            },
            immediate: true,
        },
    },

    async beforeDestroy() {
        await this.save();
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
    },

    methods: {
        ...mapActions(['runAsync']),

        async loadRecord(recordId) {
            // create a new record if requested, or load an existing one
            if (recordId === 'new' || !Number(recordId)) {
                this.record = new RequestedUser({
                    active: true,
                });
                // premature return in case of new record
                return;
            }

            // load existing record
            this.record = null;
            try {
                const collection = new RequestedUserCollection();
                await this.runAsync(() => collection.query({ id: recordId }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async reload() {
            this.loadRecord(this.record?.getId());
        },

        cancel() {
            this.$router.push({ name: 'requested_user' });
        },

        initiateDelete() {
            if (this.record) {
                this.showDelDlg = true;
            }
        },

        async deleteRec() {
            if (!this.record) {
                return false;
            }
            try {
                this.isSaving = true;
                await this.record.delete();
                this.record = null;
                this.$router.push({ name: 'requested_user' });
                return true;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.isSaving = false;
            }
        },

        async save() {
            if (!this.record) {
                return false;
            }
            if (!this.isValid) {
                return false;
            }
            if (this.record.is_generated || this.record.rejected) {
                return false;
            }

            try {
                this.isSaving = true;

                // give the child a change to do things before we save
                // e.g. save block contents
                if (this.$refs.activeChild.beforeSave) {
                    if (!(await this.$refs.activeChild.beforeSave())) {
                        return false;
                    }
                }

                const isNew = this.record?.isPhantom();

                if (this.record?.hasUnsavedData()) {
                    await this.runAsync(async () => await this.record?.save());
                }

                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'requested_user_edit', params: { id: this.record?.getId() } });
                }

                return true;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.isSaving = false;
            }
        },

        async generateUserOrganizer() {
            if ((await this.save()) !== true) {
                return false;
            }

            try {
                const response = await api().post('RequestedUser/generate', {
                    id: this.record.getId(),
                });

                if (response.data?.success) {
                    this.reload();
                } else {
                    throw new Error(response?.error?.msg);
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async rejectUser(reason = '') {
            if ((await this.save()) !== true) {
                return false;
            }
            try {
                const response = await api().post('RequestedUser/reject', {
                    id: this.record.getId(),
                    reason,
                });

                if (response.data?.success) {
                    this.reload();
                } else {
                    throw new Error(response?.error?.msg);
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        getBrowserTitle() {
            return this.browserTitle;
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 220px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}

.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}

.content {
    grid-area: content;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;
    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}

.record-title {
    color: initial;
}
</style>
